/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2013, 2022
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 * DataPlayerView
 */
define([
	'jquery',
	'underscore',
	'../VisView',
	'./DataPlayerSlider',
	'./DataPlayerPlayPause',
	'../../../widgets/livewidget/nls/StringResources',
	'text!./DataPlayerView.html'
],
function($, _, VisView, Slider, PlayPause, StringResources, template) {
	'use strict';

	const DataPlayerView = VisView.extend({

		// constants
		templateString: template,
		_dataItemAPI: null,
		_playIndex: -1,
		_currentSelectionIndex: -1,
		_mouseStartX: 0,
		_mouseOrgStartX: 0,
		_nodeVirtualPosition: 0,
		_dragging: false,


		events: {
			'clicktap .sliderLabel': 'onLabelClick',
			'keydown .sliderLabel': 'onLabelKeypress',
			'click .sliderTickForHorizontal': 'onTickClick',
			'tap .sliderTickForHorizontal': 'onTickClick',

			'touchstart .sliderNode': 'onTickDecorationMouseDown',
			'touchend .slider': 'onSliderMouseUp',

			'mousedown .sliderNode': 'onTickDecorationMouseDown',
			'mouseup .slider': 'onSliderMouseUp',

			'clicktap .playOrPause': 'onPlayOrPauseClick',
			'mousedown .playOrPause': 'preventSelection',
			'keydown .playOrPause': 'onPlayOrPauseKeypress'
		},

		init: function() {
			DataPlayerView.inherited('init', this, arguments);

			this.transaction = this.dashboardApi.getFeature('Transaction');
			this.slider = new Slider();
			this.playPauseButton = new PlayPause($(this.visEl), this.dashboardApi, this._moveToTheNextItem.bind(this));
			this.el.style.position = 'relative';
		},

		remove: function() {
			if (this.playPauseButton) {
				this.playPauseButton.clearPlayTimer();
				this.playPauseButton = null;
			}

			this.ownerWidget.dashboardApi.off('open:sharePanel', this._onSharePanelOpen, this);
			this.ownerWidget.dashboardApi.off('close:sharePanel', this._onSharePanelClose, this);

			DataPlayerView.inherited('remove', this, arguments);
		},

		/**
		* @description For dataPlayer, onDataReady will resolve when a column query for the column of interest
		* is completed.
		* @param {Object} event containing the renderContext.
		* @returns {Object} renderContext
		*/
		onDataReady: function(event) {
			//TODO. cleanup to use inherit method after fixing InfoIndicator (as a provider in VisIndicator)
			const renderContext = event.info && event.info.renderContext ? JSON.parse(JSON.stringify(event.info.renderContext)) : {};
			if (renderContext.taskRefreshed.data) {
				this.filterIndicator && this.filterIndicator.update();
				this._dataItemAPI = this.visualization.getSlots().getSlotList()[0].getDataItemList()[0];
			}
			return renderContext;
		},

		/**
		* @description Gets the default description of the View
		* @returns {Object} label and description of the View
		*/
		getDescription: function() {
			// Append the F12 key instruction to the description
			const description = DataPlayerView.inherited('getDescription', this, arguments);
			return StringResources.get('WidgetLabelWithDescripion', {
				label: description,
				description: StringResources.get('f12KeyDescription')
			});
		},

		/**
		* @description Render the Shimmer empty dataplayer as we are waiting for data results
		* @param {Object} renderInfo the renderInfo as passed from the render sequence, needed to size the view
		*/
		_renderEmptyDataPlayerWidget(renderInfo) {
			this._getSliderContainer().empty();
			this.renderIconView();

			this.resizeToWidget(renderInfo);

			// render complete so fade in
			$(this.visEl).animate({
				opacity: 1
			});
			this.playPauseButton.hide();
		},

		/**
		* @description Formats the query result data into a useable list of dataitems
		* @returns {Array} array of data items to render in the dataplayer
		*/
		_getDataItemsFromResults: function(resultData) {
			// clear up the content
			let dataItems = [];

			// data player has only one data item
			const rowSize = resultData.getResultItemList()[0].getRowCount();
			for (let row = 0; row < rowSize; row++) {
				dataItems.push(resultData.getValue(row, 0)[0]);
			}

			return dataItems;
		},

		/**
		* @description Render the view with data
		* @param {Object} renderInfo the renderInfo as passed from the render sequence, needed to size the view
		*/
		_renderDataPlayerWithResults(renderInfo) {
			this.removeIconView();

			this.resizeToWidget(renderInfo);

			const dataItems = this._getDataItemsFromResults(renderInfo.data.getResult());
			return this._renderWithDataItems(dataItems);
		},

		/**
		* @description Render the dataplayer
		* @param {object} renderInfo - the renderInfo as passed from the render sequence.
		* @returns a promise which is resolved when the render is complete
		*/
		render: function(renderInfo) {
			if (!this.isMappingComplete() || this.hasMissingFilters() || this.hasUnavailableMetadataColumns()) {
				this._renderEmptyDataPlayerWidget(renderInfo);
				return Promise.resolve(this);
			}

			this.ownerWidget.dashboardApi.on('open:sharePanel',this._onSharePanelOpen,this);
			this.ownerWidget.dashboardApi.on('close:sharePanel', this._onSharePanelClose, this);
			//NOTE: renderComplete is called from the base class visView.

			return DataPlayerView.inherited('render', this, arguments).then(
				() => this._renderDataPlayerWithResults(renderInfo)
			);
		},

		/**
		* @description Act when the user exits the dataplayer (tabs/clicks outside the widget)
		*/
		onExitContainer: function() {
			this.playPauseButton.leaveWidget();
			this.$el.find('.sliderLabel.selected').attr('tabindex', '-1');
		},

		/**
		* @description Act when the user enters the dataplayer (tabs/clicks inside the widget)
		*/
		onEnterContainer: function() {
			this.$el.find('.sliderLabel.selected').attr('tabindex', '0');
			this.playPauseButton.enterWidget();
		},

		/**
		* @description Handle keystroke events
		* @param {Object} evt keystroke event
		*/
		onLabelKeypress: function(evt) {
			const $target = $(evt.currentTarget);
			const key = evt.keyCode;
			if (key === 37 || key === 38) {
				//left
				this._moveToItem($target, $target.prevAll('.sliderLabel').first());
				evt.stopPropagation();
			} else if (key === 39 || key === 40) {
				//right
				this._moveToItem($target, $target.nextAll('.sliderLabel').first());
				evt.stopPropagation();
			}
		},

		preventSelection: function(evt) {
			evt.stopPropagation();
		},

		_moveToItem: function($current, $next) {
			$current.attr('tabindex', '-1');
			$next.click()
				.attr('tabindex', '0')
				.focus();
		},

		/**
		* @description return the filter types the dataplayer allows for the filter indicator/flyout
		* @returns {Object} filter types allowed in the filter indicator/flyout
		*/
		getFilterIndicatorSpec: function() {
			return {
				localFilters: true
			};
		},

		/**
		* @description handle a tick click (or slide to) event
		* @param event tick select event
		*/
		onTickClick: function(event) {
			// prevent click after a tap
			if (event.gesture) {
				event.gesture.preventDefault();
			}
			// get clicked item and set filter
			this._setFilterByEventTarget(event, 'sliderTickForHorizontal', false);
		},

		/**
		* @description handle a tick label select event
		* @param event label select event
		*/
		onLabelClick: function(event) {
			// get clicked item and either select or deselect
			this._setFilterByEventTarget(event, 'sliderLabel', true);
		},

		onPlayOrPauseKeypress: function(event) {
			if (event.keyCode === 13 || event.keyCode === 32) {
				this.onPlayOrPauseClick(event);
			}
		},

		/**
		* @description Handle when the play/pause button is selected
		* @param event button select event
		*/
		onPlayOrPauseClick: function(event) {
			// toggle playing mode
			if (this.playPauseButton.isPlaying()) {
				this.playPauseButton.pause();
			} else {
				this.playPauseButton.play();
			}

			event.stopPropagation();
		},

		/**
		* @description Handle when the share panel is opened as we need to pause
		*/
		_onSharePanelOpen: function() {
			if (this.playPauseButton.isPlaying()) {
				this.playPauseButton.pause();
				this.shareState = { wasPlaying: true };
			}
		},

		/**
		* @description Handle when the share panel is closed. We should resume play if needed
		*/
		_onSharePanelClose: function() {
			if(this.shareState && this.shareState.wasPlaying) {
				this.shareState = null;
				this.playPauseButton.play();
			}
		},

		/**
		* @description Event handler for the mouse or touch down on the slider node.
		* This function tracks the start position and starts the dragging logic.
		* @param {Object} event mouse down event
		*/
		onTickDecorationMouseDown: function(event) {
			// stop playing (set flag for mouse up event to know playing was just terminated or not)
			this._wasJustPlaying = false;
			if (this.playPauseButton.isPlaying()) {
				this.playPauseButton.pause();
				this._wasJustPlaying = true;
			}

			// record mouse start position and virtual node position (need virtual position because of snap)
			this._mouseStartX = this._getPageX(event);
			this._mouseOrgStartX = this._mouseStartX;
			this._nodeVirtualPosition = this.slider.getTickDecorationPosition().left + this.slider.getScrollLeftPosition();

			// setup for dragging
			this.slider.getSliderContainer().on('mousemove touchmove', this.onSliderMouseMove.bind(this));
			this._dragging = true;

			// stop the event from being passed down to underlying elements
			event.stopPropagation();

			// prevent other browser events (like real mouse down if this is a touch event)
			event.preventDefault();
		},

		/**
		* @description Event handler for the mouse or touch up on the slider node and slider area.
		* This function processes the end of drag as well as determines if a deselect should happen.
		* @param {Object} event mouse up event
		*/
		onSliderMouseUp: function(event) {
			// stop dragging as necessary
			if (this._dragging) {

				// turn off dragging
				this._dragging = false;

				// cancel the mouse move
				this.slider.getSliderContainer().off('mousemove touchmove');

				// check if has moved
				if (this._mouseOrgStartX !== this._mouseStartX) {

					// get nearest tick datavalue
					var snap = this._determineTickToSnapTo(this._nodeVirtualPosition, true);

					// set filter
					this._setFilter(snap.index);
				} else {

					// clear the selection if not playing
					if (!this._wasJustPlaying) {
						this._clearSelection();
					}
				}

				// prevent other browser events (like real mouse up if this is a touch event)
				event.preventDefault();
			}
		},

		/**
		* @description Event handler for the mouse or touch move. This occurs on the slider area.
		* It moves the slider in the case of dragging and will snap to the nearest tick
		* @param {Object} event mouse move event
		*/
		onSliderMouseMove: function(event) {
			// calculate mouse delta X
			const pageX = this._getPageX(event);
			const deltaX = (pageX - this._mouseStartX);

			// update mouse start position
			this._mouseStartX = pageX;

			// calculate new left positon
			const newLeft = this._nodeVirtualPosition + deltaX;

			// record position
			this._nodeVirtualPosition = newLeft;

			// snap to tick as necessary
			const snap = this._determineTickToSnapTo(newLeft, false);

			// move the slider with the mouse but locked to track
			this.slider.setTickDecorationLeftPosition(snap.left);

			// set the filter if a snap occurred
			if (snap.left !== newLeft) {
				this._selectTick(snap.index);
				this._setFilter(snap.index);
			}

			// prevent other browser events (like real mouse down if this is a touch event)
			event.preventDefault();
		},

		/**
		* @description Listener function for the filter change event.  This overrides the base one.
		 * This function gets the current selection and moves the slider to the
		 * corresponding location. However if the change is not due a selection, a re-render
		 * is expected by the base VisView.onChangeFilter
		 * @param {Object} event filter change event
		 * @returns {Promise}
		 */
		onChangeFilter: function(event) {
			const pageContextEvent = event.changeEvent;
			if (pageContextEvent && !pageContextEvent.brushingChanged()) {
				// Call base class to process page context changes, requery etc.
				return DataPlayerView.inherited('onChangeFilter', this, arguments);
			}
			// watch for null column or dragging
			if (this._dataItemAPI && !this._dragging) {
				// get the single filter selection
				const selections = this.getSelector().getSelections();
				const filter = selections && selections.length && selections[0].categories;

				// get entry index bases on filter
				let dataItemIndex = -1;
				if (filter && filter.length > 0) {
					dataItemIndex = this._getEntryIndex(filter[0].value);
				}

				// no need to reselect if already selected (in the case of multiple change events fired back to back)
				if (dataItemIndex !== this._currentSelectionIndex) {

					// store the new selection
					this._currentSelectionIndex = dataItemIndex;

					// position slider by value
					this._positionSlider(this._currentSelectionIndex, true, true);
				}
			}
			return Promise.resolve();
		},

		/**
		* @description Renders the UI that makes up the dataplayer
		* @param {Array} dataItems Data items to display in the dataplayer
		*/
		_renderTheBaseDataPlayer(dataItems) {
			// render the slider and play/pause button
			this.slider.render(this._dataItemAPI, dataItems, this._getSliderContainer());

			this.playPauseButton.render(this.slider.getSliderSize().height, !(dataItems && dataItems.length > 1)/*isDisabled*/);
		},

		/**
		* @description Renders the dataplayer when data items are available. It calculates
		* placement of ticks and labels while hidden and then animates a fade in to
		* show the control.  It tracks data entries in a global array.
		* @param {Array} dataItems data results to populate the slider
		**/
		_renderWithDataItems: function(dataItems) {
			this._renderTheBaseDataPlayer(dataItems);

			const selections = this.getSelector().getSelections();
			const filter = selections && selections.length && selections[0].categories;

			let dataIndex = -1;
			let showDecoration = false;
			if (filter && filter.length > 0) {
				dataIndex = this._getEntryIndex(filter[0].value);
				dataIndex = dataIndex !== -1 ? dataIndex : 0;
				showDecoration = true;
			}
			this._currentSelectionIndex = dataIndex;
			this._positionSlider(dataIndex, false, showDecoration);
			this.playPauseButton.show();

			// render complete so fade in
			return new Promise(complete =>
				$(this.visEl).animate({
					opacity: 1
				}, {
					complete
				})
			);
		},

		/**
		* @description Find the playerEntry matching the dataItemValue specified
		* @param {String} dataItemValue value of the data entry to look up
		* @return {Integer} The index of the entry or -1 by if not found.
		**/
		_getEntryIndex: function(dataItemValue) {
			let index = -1;
			const entry = this.slider.getPlayerEntryWithValue(dataItemValue);

			if (entry) {
				index = entry.index;
			}

			return index;
		},

		/**
		* @description Move the slider to the tick for the specified index.
		* It can optionally be animated and tick decoration made visible. This function
		*	also sets the play index counter to the next tick down the line.
		*	@param {Integer} dataItemIndex index of the data entry to look up
		*	@param {Boolean} animate should the movement be animated
		*	@param {Boolean} showNode should the tick decoration be visible
		**/
		_positionSlider: function(dataItemIndex, animate, showDecoration) {
			// if no dataItemIndex then no selection
			if (dataItemIndex < 0) {
				this.slider.hideTickDecoration();
				this._selectTick(-1);
				return;
			}

			this._playIndex = this.slider.positionSlider(dataItemIndex, animate, showDecoration);

			// select the label
			if (showDecoration) {
				this._selectTick(dataItemIndex);
			}
		},

		_clearCurrentlySelectedTickAndRetrieveTabIndex() {
			// remove all selections
			const $selected = $(this.visEl).find('.sliderLabel.selected');
			const tabIndex = $selected.attr('tabindex');

			$selected.removeClass('selected');
			$selected.attr('tabindex', -1);
			$selected.attr('aria-selected', 'false');
			return tabIndex;
		},

		_selectTickAtIndexAndSetTabIndex(dataItemIndex, tabIndex) {
			// select if index given
			if (dataItemIndex >= 0) {
				$(this.visEl).find('.sliderLabel[data-item-index="' + dataItemIndex + '"]')
					.addClass('selected')
					.attr('aria-selected', 'true')
					.attr('tabindex', tabIndex);
			}
		},

		/**
		* @description Highlights the label for the specified index, de-selects all others.
		*	@param {Integer} dataItemIndex index of the entry to select
		**/
		_selectTick: function(dataItemIndex) {
			const tabIndex = this._clearCurrentlySelectedTickAndRetrieveTabIndex();
			this._selectTickAtIndexAndSetTabIndex(dataItemIndex, tabIndex);
		},

		/**
		* @description Sets a filter for the index specified while clearing all existing
		* filters before setting the new one.
		*	@param {Integer} dataItemIndex index of the data item to filter on
		**/
		_setFilter: function(dataItemIndex) {
			// Clear any tooltips or other toolbars when we change the data to avoid stale info in our view
			this.ownerWidget.dashboardApi.triggerDashboardEvent('widget:hideToolbar');

			const currentEntryValue = this.slider.getValueAtIndex(dataItemIndex);
			const transactionToken = this.transaction.startTransaction();
			this.getSelector().clearAll(transactionToken);
			this.getSelector().select([{
				categories:[{
					dataItemId: this._dataItemAPI.getId(),
					columnId: this._dataItemAPI.getColumnId(),
					label: currentEntryValue.label,
					value: currentEntryValue.value
				}]
			}], transactionToken);
			this.transaction.endTransaction(transactionToken);
		},

		_getDataItemIndexFromEvent(event, targetName) {
			// get clicked item and either select or deselect
			const el = this.getTarget(event.target, targetName);
			return el.getAttribute('data-item-index');
		},

		_reactToSelectionOfCurrentNode(allowToggle) {
			// deselect
			if (allowToggle && !this.playPauseButton.isPlaying()) {
				this._clearSelection();
			}
		},

		_reactToSelectionOfNewNode(dataItemIndex) {
			// immediately show label as selected
			this._selectTick(dataItemIndex);

			// set filter
			this._setFilter(dataItemIndex);
		},

		/**
		* @description Called when a tick or tick label is selected. It will filter on
		* the data item represented by the selection.
		* @param {Event} event selection event
		* @param {String} targetName target to filter on (tick label or tick itself)
		* @param {Boolean} allowToggle Should the filter toggle if already set
		**/
		_setFilterByEventTarget: function(event, targetName, allowToggle) {
			// get clicked item and either select or deselect
			let dataItemIndex = this._getDataItemIndexFromEvent(event, targetName);
			if (dataItemIndex) {
				// make sure the index is an integer
				dataItemIndex = parseInt(dataItemIndex, 10);

				// select if not selected
				if (dataItemIndex !== this._currentSelectionIndex) {
					this._reactToSelectionOfNewNode(dataItemIndex);
				} else {
					this._reactToSelectionOfCurrentNode(allowToggle);
				}
			}
		},

		/**
		* @description Move the data player to the next tick, wrapping if necessary
		**/
		_moveToTheNextItem: function() {
			// sanity check for data
			if (!(this._dataItemAPI && this.slider.hasPlayerEntries())) {
				return;
			}

			// when slider position is not set, start playing with index 0 instead of -1;
			if (this._playIndex === -1){
				this._playIndex = 0;
			}

			// set global filter
			this._setFilter(this._playIndex, true);
		},

		/**
		* @description Get the slider container.
		* @returns {JQuery Element} the slider container JQuery element
		**/
		_getSliderContainer: function() {
			return $(this.visEl).find('.slider');
		},


		/**
		* @description Clear the current selection
		**/
		_clearSelection: function() {
			if (this._currentSelectionIndex !== -1) {
				this.getSelector().clearAll();
			}
			// clear the slider
			this._positionSlider(-1);

			// clear current tick selection
			this._selectTick(-1);
			this._currentSelectionIndex = -1;
		},

		/**
		* @description Determine which tick is closest to the current position.
		* @param {Double} leftPosition the left position of the slider node
		* @param {Boolean} splitThreshold is the threshold to determine closest tick pre-defined or split between ticks.
		* @returns {Object} left position and tick index value
		**/
		_determineTickToSnapTo: function(leftPosition, splitThreshold) {
			// clear current selection so that it will snap correctly
			this._currentSelectionIndex = -1;
			return this.slider.determineTickToSnapTo(leftPosition, splitThreshold);
		},

		/**
		* @description Returns the pageX position between a touch event and regular mouse event.
		* @param {Event} event
		* @returns the pageX position of the event
		**/
		_getPageX: function(event) {
			return event.type.substr(0, 5) !== 'touch' ? event.pageX : event.originalEvent.touches[0].pageX;
		}
	});

	return DataPlayerView;
});
